import { z } from 'zod';

import type { FmMediaAsset } from './mediaRegistry';

const semanticIdentity = z.string().regex(
  /^(?!stock\.)(?!https?:)(?!.*(?:\/|\\|\.[a-z0-9]{2,5}$))[a-z][a-z0-9]*(?:[.-][a-z0-9]+)+$/,
  'Media identity must describe meaning, not a source, URL, object key, or filename',
);

const derivativeSchema = z.object({
  url: z.string().min(1),
  width: z.number().int().positive(),
  format: z.enum(['avif', 'webp', 'jpeg', 'png', 'svg']),
  checksum: z.string().min(8),
});

const provenanceSchema = z.object({
  source: z.enum(['skogiq-app', 'skogiq-data-render', 'generated', 'licensed-stock', 'manual-upload']),
  createdAt: z.iso.datetime(),
  screenshotRoute: z.string().optional(),
  screenshotSeed: z.string().optional(),
  generatedPrompt: z.string().optional(),
  generatedModel: z.string().optional(),
  dataDependencies: z.array(z.string()).optional(),
  creator: z.string().optional(),
  sourceUrl: z.url().optional(),
  licenseUrl: z.url().optional(),
  retrievedAt: z.iso.datetime().optional(),
  licenseVersion: z.string().min(1).optional(),
  replacementPolicy: z.enum(['stable', 'regenerate-on-release', 'regenerate-on-data-change', 'temporary']).optional(),
}).superRefine((value, context) => {
  if (value.source !== 'licensed-stock') return;
  for (const field of ['creator', 'sourceUrl', 'licenseUrl', 'retrievedAt', 'licenseVersion'] as const) {
    if (!value[field]) {
      context.addIssue({
        code: 'custom',
        path: [field],
        message: `licensed-stock requires ${field}`,
      });
    }
  }
});

const cropProfileSchema = z.object({
  aspect: z.enum(['wide', 'square', 'portrait', 'panorama']),
  focalPoint: z.string().min(1),
  note: z.string().min(1),
  scale: z.number().min(1).max(2).optional(),
});

const semanticContextSchema = z.object({
  species: z.enum(['pine', 'spruce', 'birch', 'deciduous', 'mixed']).optional(),
  developmentClass: z.enum(['A0', 'T1', 'T2']).optional(),
  landClass: z.literal('nonproductive-peatland').optional(),
  structure: z.enum([
    'pine-overstory-spruce-regeneration',
    'birch-overstory-spruce-regeneration',
  ]).optional(),
  condition: z.enum(['conifer-mortality', 'broken-trees']).optional(),
  illustrative: z.literal(true),
});

const deliveryAssetSchema = z.object({
  identity: semanticIdentity,
  kind: z.enum(['image', 'video', 'animation', 'map-poster', 'screenshot', 'poster']),
  objectKey: z.string().min(1),
  contentVersion: z.string().min(1),
  checksum: z.string().min(8),
  availability: z.enum(['available', 'unavailable']),
  derivatives: z.array(derivativeSchema).min(1),
  alt: z.string().min(1),
  credit: z.string().min(1).optional(),
  license: z.string().min(1).optional(),
  requiresCredit: z.boolean().default(false),
  aspect: z.enum(['wide', 'square', 'portrait', 'panorama']),
  focalPoint: z.string().optional(),
  dominantTone: z.enum(['light', 'dark', 'green', 'warm', 'cool']).optional(),
  // partialRecord: keys constrained to governed roles, any subset allowed —
  // matches FmMediaAsset's Partial<Record<...>> and the backend Pydantic
  // manifest contract, which never requires all roles per asset.
  cropProfiles: z.partialRecord(
    z.enum(['background', 'split', 'card', 'entity-card', 'mobile']),
    cropProfileSchema,
  ).optional(),
  semanticContext: semanticContextSchema.optional(),
  provenance: provenanceSchema,
}).superRefine((value, context) => {
  if (value.provenance.source !== 'licensed-stock') return;
  if (!value.license) {
    context.addIssue({
      code: 'custom',
      path: ['license'],
      message: 'licensed-stock requires license',
    });
  }
  if (value.requiresCredit && !value.credit) {
    context.addIssue({
      code: 'custom',
      path: ['credit'],
      message: 'licensed-stock with requiresCredit=true requires visible credit',
    });
  }
});

export const mediaDeliveryManifestSchema = z.object({
  schemaVersion: z.literal(1),
  catalogVersion: z.string().min(1),
  previousCatalogVersion: z.string().nullable(),
  publishedAt: z.iso.datetime(),
  allowedDeliveryHosts: z.array(z.string().min(1)).default([]),
  assets: z.array(deliveryAssetSchema),
});

export type MediaDeliveryManifest = z.infer<typeof mediaDeliveryManifestSchema>;

function withVersion(url: string, version: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(version)}`;
}

export function parseMediaDeliveryManifest(input: unknown): MediaDeliveryManifest {
  const manifest = mediaDeliveryManifestSchema.parse(input);
  for (const asset of manifest.assets) {
    for (const derivative of asset.derivatives) {
      const url = new URL(derivative.url, window.location.origin);
      if (url.origin !== window.location.origin && !manifest.allowedDeliveryHosts.includes(url.host)) {
        throw new Error(`Media delivery host is not allowlisted for ${asset.identity}`);
      }
    }
  }
  return manifest;
}

export function deliveryAssetToMediaAsset(
  asset: MediaDeliveryManifest['assets'][number],
): FmMediaAsset | null {
  if (asset.availability !== 'available') return null;
  const derivatives = [...asset.derivatives].sort((left, right) => left.width - right.width);
  const largest = derivatives[derivatives.length - 1];
  return {
    key: asset.identity,
    kind: asset.kind,
    src: withVersion(largest.url, asset.contentVersion),
    srcSet: derivatives.map((item) => `${withVersion(item.url, asset.contentVersion)} ${item.width}w`).join(', '),
    alt: asset.alt,
    credit: asset.credit,
    license: asset.license,
    requiresCredit: asset.requiresCredit ?? false,
    aspect: asset.aspect,
    focalPoint: asset.focalPoint,
    dominantTone: asset.dominantTone,
    cropProfiles: asset.cropProfiles,
    semanticContext: asset.semanticContext,
    provenance: asset.provenance,
    webglPolicy: { webglAllowed: false },
    delivery: {
      catalogVersion: asset.contentVersion,
      objectKey: asset.objectKey,
      checksum: asset.checksum,
    },
  };
}
